import { Component, OnInit, OnDestroy, ElementRef } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Store, Select } from '@ngxs/store';
import { ProprieteState } from '../../propriete.state';
import { FormGroup, FormBuilder, Validators, FormControl } from '@angular/forms';
import {
  MAX_INPUT_TAXES_SCOLAIRES,
  MAX_INPUT_TAXES_MUNICIPALES,
  MAX_INPUT_COUT_ENERGIE,
  MAX_INPUT_FRAIS_COPROPRIETE
} from '../../../../configs/input.config';
import { FormPropertyService } from '../../services/form-property-service/form-property.service';
import { FormValidationService } from '../../../shared/services/form-validation.service';
import { ShowLoader, HideLoader, ShowPopup } from '../../../shared/personne.state';
import { ProprieteHelper } from '../../modeles/ProprieteHelper';
import { Observable, Subscription } from 'rxjs';
import { ErrorMessageValidation } from '../../../shared/modeles/error-message-validation.modele';
import { DonneReferenceService } from '../../../shared/services/donne-reference.service';
import { ValidationErrors } from 'validatorjs';
import { SetCurrentCodePage } from '../../../shared/navigation.state';
import { ProprieteModele, AdresseModele } from '../../modeles/proprieteModel';

@Component({
  selector: 'app-formulaire-propriete',
  templateUrl: './formulaire-propriete.component.html',
  styleUrls: ['./formulaire-propriete.component.scss'],
  providers: [FormPropertyService],
  standalone: false
})
export class FormulaireProprieteComponent implements OnInit, OnDestroy {
  @Select(ProprieteState.selectedProperty)
  selectedProperty$: Observable<ProprieteModele>;
  selectedProperty: ProprieteHelper;
  selectedPropertySubscription: Subscription;

  @Select(ProprieteState.proprietes)
  proprietes$: Observable<ProprieteModele[]>;
  listeProprietes: Array<ProprieteModele>;
  listPropertySubscription: Subscription;

  isLoaded: boolean;
  formMode: string;
  currentId: string;
  isProcessing: boolean;

  etapeUnForm: FormGroup;
  etapeDeuxForm: FormGroup;
  propertyForm: FormGroup;
  fraisRenovationsMaxInput: number;

  isLangFr: boolean;
  contactPhoneNumber: any;

  errorMessageValidation: ErrorMessageValidation[];
  isErrors: boolean;
  numeroMontreal: string;

  activePosteCanada = true;
  typePropriete: Array<any> = [];

  address: AdresseModele = {
    adresseFormattee: null,
    codePostal: null,
    numeroCivique: null,
    pays: null,
    rue: null,
    territoire: null,
    typeUnite: null,
    unite: null,
    ville: null
  };

  readonly maxInputTaxesScolaires = MAX_INPUT_TAXES_SCOLAIRES;
  readonly maxInputTaxesMunicipales = MAX_INPUT_TAXES_MUNICIPALES;
  readonly maxInputCoutEnergie = MAX_INPUT_COUT_ENERGIE;
  readonly maxInputFraisCopropriete = MAX_INPUT_FRAIS_COPROPRIETE;

  constructor(
    private readonly _activeRoute: ActivatedRoute,
    private readonly _store: Store,
    private readonly _formBuilder: FormBuilder,
    private readonly _formPropertyService: FormPropertyService,
    private readonly _formValidationService: FormValidationService,
    private readonly _donneesReferenceService: DonneReferenceService,
    private readonly _el: ElementRef
  ) {}

  ngOnInit() {
    this.formMode = 'create';
    this.numeroMontreal = this._donneesReferenceService.numerosDemandeSupport.montreal;
    this._formPropertyService.setCurrentPage();
    this.isLangFr = this._formPropertyService.isFrench();
    this.typePropriete = this._donneesReferenceService.typeProprieteDonneReference;

    this.initForm();

    this._activeRoute.params
      .subscribe(({ id }) => {
        if (id) {
          this.formMode = 'edit';
          this.currentId = id;
          this._formPropertyService.fetchData(id);
        } else {
          this._formPropertyService.pushDebuteEvent();
        }
        this.contactPhoneNumber = this._formPropertyService.contactPhoneNumber;
      })
      .unsubscribe();

    this.fetchSelectedProperty();
    this.fetchListeProprietes();

    this._store.dispatch(new SetCurrentCodePage('creation'));
  }

  ngOnDestroy(): void {
    if (this.selectedPropertySubscription) {
      this.selectedPropertySubscription.unsubscribe();
    }

    if (this.listPropertySubscription) {
      this.listPropertySubscription.unsubscribe();
    }
  }

  private fetchSelectedProperty() {
    this.selectedPropertySubscription = this.selectedProperty$.subscribe((propertySelected: ProprieteModele) => {
      this.patchFormWithProperty(propertySelected);

      if (propertySelected) {
        this.selectedProperty = new ProprieteHelper(propertySelected);
        this.patchAddress(propertySelected.proprieteAdresse);
        this.isLoaded = true;
      }
    });
  }

  private fetchListeProprietes() {
    this.listPropertySubscription = this.proprietes$.subscribe(proprietes => {
      this.listeProprietes = JSON.parse(JSON.stringify(proprietes));
    });
  }

  updateMontantFraisRenovationsMaxInput(): void {
    this.fraisRenovationsMaxInput =
      ProprieteHelper.MAX_TOTAL_PRICE_EXCLUSIVE - (this.propertyForm.get('prix').value || 0) - 1;
  }

  initForm(): void {
    const uniqueAddressValidator = this.validateUniqueAddress();
    this.propertyForm = this._formBuilder.group(
      {
        analyseEcartPrequalification: [null], // hidden
        id: [null], // hidden
        prix: [null, [Validators.required]],
        montantFraisRenovations: [null],
        montantTaxesScolaires: [null, [Validators.required]],
        montantTaxesMunicipales: [null, [Validators.required]],
        montantCoutEnergie: [null, [Validators.required]],
        montantFraisCopropriete: [null],
        titre: [null],
        proprieteAdresse: [null],
        type: [null],
        notePersonnelle: [null]
      },
      {
        updateOn: 'blur',
        validators: uniqueAddressValidator
      }
    );
  }

  private patchFormWithProperty(property: ProprieteModele): void {
    if (this.propertyForm && property) {
      this.propertyForm.patchValue(property);
      this.updateMontantFraisRenovationsMaxInput();
    }
  }

  private patchAddress(address) {
    if (this.propertyForm && address && address.adresseFormattee) {
      this.address = address;
      this.propertyForm.patchValue({ proprieteAdresse: this.address.adresseFormattee });
    }
  }

  checkErrors(formGroup: FormGroup): boolean {
    const form = Object.values(formGroup.controls);
    return form.some((control: FormControl) => !!control.errors);
  }

  getErrorControls(formGroup: FormGroup) {
    const errorControls = Object.entries(formGroup.controls).filter(([_, value]) => value.touched && !!value.errors);
    return (
      !!errorControls &&
      errorControls.map(([key, _]) => {
        const text: HTMLElement = this._el.nativeElement.querySelector(`label[for=${key}]`);
        return {
          inputId: key,
          inputLabel: !!text ? text.innerText : null
        };
      })
    );
  }

  saveProperty(): void {
    this._formValidationService.markAllAsTouched(this.propertyForm);
    this.errorMessageValidation = this.getErrorControls(this.propertyForm);
    this.isErrors = this.checkErrors(this.propertyForm);
    if (this.propertyForm.valid) {
      this._store.dispatch(new ShowLoader());

      if (this.isEditingOldProperty && this.address) {
        this.propertyForm.patchValue({ proprieteAdresse: this.address });
      }

      this._formPropertyService
        .submitToApi(this.propertyForm, this.formMode)
        .toPromise()
        .then(async () => {
          await this._formPropertyService.navigateToReturnUrl();
          if (this.formMode === 'create') {
            this._formPropertyService.pushCreatedEvent(this.propertyForm.value);
          }
          this._store.dispatch(new HideLoader());
        })
        .catch(() => {
          this._store.dispatch(new HideLoader());
          this._store.dispatch(new ShowPopup('propriete-error', this.formMode));
        });
    }
  }

  get isEditingOldProperty() {
    return this.formMode === 'edit' && this.selectedProperty && this.selectedProperty.isOldPropriete;
  }

  async cancel() {
    await this._formPropertyService.navigateToReturnUrl();
  }

  updateAddress(address) {
    this.address = address;
  }

  validateUniqueAddress() {
    return (control: FormGroup): ValidationErrors | null => {
      if (this.listeProprietes && this.isEditingOldProperty && this.address.adresseFormattee) {
        for (const property of this.listeProprietes) {
          if (
            property.proprieteAdresse &&
            property.proprieteAdresse.adresseFormattee === control.value.proprieteAdresse &&
            property.id !== this.selectedProperty.propriete.id
          ) {
            control.get('proprieteAdresse').setErrors({ ['sameAddress']: true });
          }
        }
      }
      return null;
    };
  }

  get submitButtonLabel() {
    return this.formMode === 'create' ? 'propriete.creation.titre' : 'btn.sauvegarder';
  }
}
